// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useRef, useState } from 'react';
import styles from './styles';
import ArtifactErrorBoundary from 'Components/ArtifactErrorBoundary';
import SpatialFeatureChart, { SpatialChartSpec } from './SpatialFeatureChart';

export default function PopupWindow({
  x,
  y,
  attributes,
  index,
  totalCount,
  onPrev,
  onNext,
  onClose,
  parentRef,
}) {
  const s = styles('', {}, 0, false, false, false);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState({ left: x, top: y, transform: '' });
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Clamp the popup inside the parent bounds on a new anchor (x/y) AND when the shown feature changes
  // (Next/Prev advances `index`): a feature with more attribute rows makes the popup taller, and without
  // re-clamping it would overflow the parent's bottom edge. Re-anchoring to x/y on page is intended (the
  // popup belongs to the clicked location).
  useEffect(() => {
    if (!popupRef.current) return;
    const parent = popupRef.current.offsetParent;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    const clampedX = Math.max(0, Math.min(x, parentRect.width - popupRect.width));
    const clampedY = Math.max(0, Math.min(y, parentRect.height - popupRect.height));
    dragging.current = false;
    setPosition({ left: clampedX, top: clampedY, transform: 'translate(0,0)' });
  }, [x, y, index]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!popupRef.current) return;
    dragging.current = true;
    const popupRect = popupRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - popupRect.left,
      y: e.clientY - popupRect.top,
    };
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging.current || !popupRef.current) return;
    const parentRect = popupRef.current.offsetParent.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();
    let newX = e.clientX - parentRect.left - dragOffset.current.x;
    let newY = e.clientY - parentRect.top - dragOffset.current.y;
    newX = Math.max(0, Math.min(newX, parentRect.width - popupRect.width));
    newY = Math.max(0, Math.min(newY, parentRect.height - popupRect.height));
    setPosition({ left: newX, top: newY, transform: 'translate(0,0)' });
  };

  useEffect(() => {
    const onMouseUp = () => { dragging.current = false; };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // `khora_chart` is a JSON string a Khora spatial layer bakes onto each feature — rendered as a
  // declarative chart child below (never as a raw table row, and never as native popup HTML).
  const INTERNAL_KEYS = new Set(['geometry', 'layerName', 'layerColor', 'Layer Name', 'khora_chart', 'khora_analysis']);

  // Parse a khora chart-spec JSON string (`khora_chart` = a Khora layer's own per-feature chart;
  // `khora_analysis` = analysis Khora appended to an EXISTING feature's popup). Malformed → null.
  const parseChartSpec = (raw: unknown): SpatialChartSpec | null => {
    if (!raw) return null;
    try {
      const spec = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return spec && Array.isArray(spec.data) && spec.data.length ? spec : null;
    } catch {
      return null; // malformed chart JSON degrades to the attribute table only
    }
  };
  const featureChart = parseChartSpec(attributes.khora_chart);
  const analysisChart = parseChartSpec(attributes.khora_analysis);
  const analysisLabel = analysisChart
    ? `Khora Analysis${analysisChart.title ? ` · ${analysisChart.title}` : ''}`
    : '';

  // Only http(s) URLs may become a clickable link/image — never javascript:/data:/vbscript:. A
  // feature attribute value (which for a Khora add_spatial_layer can be ANY model-chosen SELECT
  // value) that isn't a safe http(s) URL is rendered as plain escaped text instead, so a
  // `javascript:` payload smuggled into an attribute can't execute. (encodeURI does NOT do this — it
  // leaves the scheme intact.)
  const safeHttpUrl = (raw: string): string | null => {
    try {
      const parsed = new URL(raw, window.location.origin);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
    } catch {
      return null;
    }
  };

  // A SAFE http(s) URL that points at an image file (jpg/png/gif/webp/bmp), ignoring any query string.
  const IMAGE_URL = /\.(jpe?g|png|gif|webp|bmp)(?:\?.*)?$/i;
  const asImageUrl = (raw: string): string | null => {
    const safe = safeHttpUrl(raw.trim());
    if (!safe) return null;
    try { return IMAGE_URL.test(new URL(safe).pathname) ? safe : null; } catch { return null; }
  };

  // Render a field value: if it's one or more newline-separated IMAGE URLs — from the gdb's aliased "Pole
  // Image" field OR a pinned Analysis Layer's raw pole_image / "Photo N" field (detected by VALUE, so the
  // field NAME doesn't matter) — show each as a thumbnail that opens the full image in a new tab. A broken
  // image degrades to alt text. Anything else is plain escaped text, so a non-http(s) value (e.g. a
  // javascript: payload smuggled into an attribute) never becomes a clickable link. URLs can contain spaces
  // (e.g. "44_Photo 1.jpg"), so split on newlines ONLY, never whitespace.
  const renderValue = (key: string) => {
    const text = String(attributes[key] ?? '');
    if (!text.trim()) return text;
    const parts = text.split('\n').map((part) => part.trim()).filter(Boolean);
    const images = parts.map(asImageUrl).filter((url): url is string => !!url);
    if (!images.length || images.length !== parts.length) return text;
    return (
      <span style={s.popupImageGroup}>
        {images.map((href) => (
          <a key={href} href={href} target="_blank" rel="noopener noreferrer">
            <img src={href} alt="Photo" style={s.popupImage} />
          </a>
        ))}
      </span>
    );
  };

  const dynamicRows = Object.keys(attributes)
    .filter((key) => !INTERNAL_KEYS.has(key))
    .map((key, i) => (
      <tr key={key} style={s.popupTableRow(i)}>
        <td style={s.popupTableCellKey}>{key}</td>
        <td style={s.popupTableCellValue}>{renderValue(key)}</td>
      </tr>
    ));

  // If no fields exist, show only Layer Name row, otherwise show only the fields
  const rows = dynamicRows.length > 0 ? dynamicRows : [
    <tr key="__layerName__" style={s.popupTableRow(0)}>
      <td style={s.popupTableCellKey}>Layer Name</td>
      <td style={s.popupTableCellValue}>{attributes.layerName || 'Feature'}</td>
    </tr>,
  ];

  return (
    <div ref={popupRef} style={s.popupContainer(position)}>
      <div style={{ ...s.popupHeader }} onMouseDown={handleMouseDown}>
        <div style={s.popupLayerInfo}>
          <div style={s.layerColorBox(attributes.layerColor)}></div>
          <span>{attributes.layerName || 'Feature'}</span>
        </div>
        <div style={s.popupControls}>
          <button disabled={index === 0} onClick={onPrev} style={s.prev_btn}>‹</button>
          <span>{index + 1} of {totalCount}</span>
          <button disabled={index === totalCount - 1} onClick={onNext} style={s.next_btn}>›</button>
          <span style={s.popupClose} onClick={onClose}>×</span>
        </div>
      </div>
      <div style={s.popupContentWrapper}>
        {featureChart && (
          <div style={{ marginBottom: 8 }}>
            <ArtifactErrorBoundary label="chart">
              <SpatialFeatureChart spec={featureChart} width={260} height={150} />
            </ArtifactErrorBoundary>
          </div>
        )}
        <table style={s.popupTable}>
          <tbody>{rows}</tbody>
        </table>
        {analysisChart && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid rgba(1,161,221,0.3)' }}>
            <div style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#01A1DD',
              marginBottom: 4,
            }}
            >
              {analysisLabel}
            </div>
            <ArtifactErrorBoundary label="analysis">
              <SpatialFeatureChart spec={analysisChart} width={260} height={150} />
            </ArtifactErrorBoundary>
          </div>
        )}
      </div>
    </div>
  );
}